import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import EditIcon from '@mui/icons-material/Edit';
import LiveHelpIcon from '@mui/icons-material/LiveHelp';
import PersonIcon from '@mui/icons-material/Person';
import PowerIcon from '@mui/icons-material/PowerSettingsNew';
import SaveIcon from '@mui/icons-material/Save';
import { styled } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import axios from 'axios';
import { useTypedSelector } from 'hooks';
import Image from 'next/image';
import router, { useRouter } from 'next/router';
import * as React from 'react';
import { FaGithub, FaLinkedin, FaTwitter, FaWhatsapp } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import { auth, cleanAuth, toggleEditMode } from 'store';
import { API } from '_helpers';
import CustomInput from '../../../customInput';

const Item = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  position: 'sticky',
  top: 80,
}));

const Identity: React.FC<IProps<IDeveloper>> = ({ data }) => {
  const { locale } = useRouter();
  const dispatch = useDispatch();

  const [current, setCurrent] = React.useState('');
  const [profile, setProfile] = React.useState<any>();

  const {
    github,
    whatsapp,
    twitter,
    linkedin,
    why,
    loading,
    _id,
    name,
    ...rest
  } = useTypedSelector((state) => state.auth) as IDevIdentity & IInit;

  const { modeEditIdentity } = useTypedSelector(
    (state) => state.toggleEditMode
  );

  const onLogout = async () => {
    router.push('/');
    await axios.post('/api/logout');
  };

  const handleChangeProfile = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    e.preventDefault();
    if (!e.target.files) return;
    setCurrent('identity');
    dispatch(auth({ loading: true, error: '' }));
    setProfile(URL.createObjectURL(e.target.files[0]));
    const formData = new FormData();
    formData.append('files', e.target.files[0]);
    formData.append('ref', 'developer');
    formData.append('refId', _id ? _id : 'id');
    formData.append('field', 'media');

    const upload = await axios.post(`${API}/upload`, formData, {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${data.jwt}`,
      },
    });
    dispatch(auth({ loading: false, error: '' }));
    setCurrent('');
    setProfile(API + upload?.data[0].url);
  };

  const onEdit = () => {
    dispatch(toggleEditMode({ modeEditIdentity: true }));
  };

  React.useEffect(() => {
    if (data?.media?.url) {
      setProfile(`${API}${data?.media?.url}`);
    }
    dispatch(
      auth({
        loading: false,
        github: data?.RS?.github,
        whatsapp: data?.RS?.whatsapp,
        twitter: data?.RS?.twitter,
        linkedin: data?.RS?.linkedin,
        why: data?.why,
        name: data?.name,
        _id: data?._id,
        ...rest,
      })
    );
    return (): void => {
      dispatch(cleanAuth());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async () => {
    try {
      dispatch(auth({ loading: true, error: '' }));
      setCurrent('identity');
      const res = await axios.put(
        `${API}/developers/${data._id}`,
        {
          RS: { github, whatsapp, twitter, linkedin },
          why,
          name,
        },
        {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${data.jwt}`,
          },
        }
      );

      setCurrent('');
      dispatch(toggleEditMode({ modeEditIdentity: false }));
      dispatch(
        auth({
          loading: false,
          github: res?.data?.RS?.github,
          whatsapp: res?.data?.RS?.whatsapp,
          twitter: res?.data?.RS?.twitter,
          linkedin: res?.data?.RS?.linkedin,
          why: res?.data?.why,
          name: res?.data?.name,
          ...rest,
        })
      );
    } catch (error: any) {
      return dispatch(
        auth({
          loading: false,
          error: 'Error',
        })
      );
    }
  };

  return (
    <Item>
      <Stack spacing={2}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ mb: 2, position: 'relative' }}
        >
          <Paper
            variant="outlined"
            sx={{
              width: 100,
              height: 100,
              borderRadius: 50,
              overflow: 'hidden',
              cursor: 'pointer',
            }}
          >
            <Box component="label" htmlFor="contained-button-file">
              <Box
                component="input"
                type="file"
                disabled={!modeEditIdentity}
                accept="image/*"
                onChange={handleChangeProfile}
                sx={{ display: 'none' }}
                id="contained-button-file"
              ></Box>
              <IconButton
                component="span"
                sx={{
                  width: '100%',
                  height: '100%',
                }}
              >
                {profile ? (
                  <Image
                    alt="profile"
                    src={profile}
                    layout="fill"
                    objectFit="cover"
                  />
                ) : (
                  <AddPhotoAlternateIcon fontSize="large" />
                )}
              </IconButton>
            </Box>
          </Paper>
          <Stack spacing={1}>
            <Typography component="h3" variant="h3">
              {name}
            </Typography>
            <Typography>{data.email}</Typography>
          </Stack>
          <IconButton
            sx={{ position: 'absolute', right: 2, top: 2 }}
            onClick={onLogout}
          >
            <Tooltip
              arrow
              title={locale === 'fr' ? 'Se déconnecter' : 'Logout'}
            >
              <PowerIcon color="primary" />
            </Tooltip>
          </IconButton>
        </Stack>
        {modeEditIdentity ? (
          <>
            <CustomInput
              placeholder="Name"
              name="name"
              value={name}
              icon={<PersonIcon />}
            />
            <CustomInput
              placeholder="Whatsapp"
              name="whatsapp"
              value={whatsapp}
              icon={<FaWhatsapp />}
            />
            <CustomInput
              placeholder="LinkedIn"
              name="linkedin"
              value={linkedin}
              icon={<FaLinkedin />}
            />
            <CustomInput
              placeholder="Twitter"
              name="twitter"
              value={twitter}
              icon={<FaTwitter />}
            />
            <CustomInput
              placeholder="Github"
              name="github"
              value={github}
              icon={<FaGithub />}
            />
            <CustomInput
              name="why"
              value={why}
              placeholder={
                locale === 'fr'
                  ? 'Pourquoi aimeriez-vous vous joindre à nous?'
                  : 'Why would you like to join us?'
              }
              rows={3}
              multiline
            />

            <Box>
              <Button
                variant="contained"
                disableElevation
                onClick={onSubmit}
                startIcon={<SaveIcon />}
              >
                {current === 'identity' && loading
                  ? locale === 'fr'
                    ? 'Chargement...'
                    : 'Loading...'
                  : locale === 'fr'
                  ? 'Enregistrer'
                  : 'Save'}
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ px: 2 }}
            >
              <Box sx={{ mt: 0.3 }}>
                <FaWhatsapp />
              </Box>
              <a
                href={`tel:${whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {whatsapp}
              </a>
            </Stack>
            <Stack direction="row" spacing={2} sx={{ px: 2 }}>
              <Box sx={{ mt: 0.3 }}>
                <FaLinkedin />
              </Box>
              <a href={`${linkedin}`} target="_blank" rel="noopener noreferrer">
                {linkedin}
              </a>
            </Stack>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ px: 2 }}
            >
              <Box sx={{ mt: 0.3 }}>
                <FaTwitter />
              </Box>
              <a href={`${twitter}`} target="_blank" rel="noopener noreferrer">
                {twitter}
              </a>
            </Stack>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ px: 2 }}
            >
              <Box sx={{ mt: 0.3 }}>
                <FaGithub />
              </Box>
              <a href={`${github}`} target="_blank" rel="noopener noreferrer">
                {github}
              </a>
            </Stack>
            <Divider />
            <Stack direction="row" spacing={2} sx={{ px: 2 }}>
              <Box sx={{ mt: 0.3 }}>
                <LiveHelpIcon />
              </Box>
              <Typography textAlign="justify">{why}</Typography>
            </Stack>
            <Divider />
            <Box>
              <Button
                disableElevation
                onClick={onEdit}
                startIcon={<EditIcon />}
              >
                {locale === 'fr' ? 'Editer' : 'Edit'}
              </Button>
            </Box>
          </>
        )}
      </Stack>
    </Item>
  );
};

export default Identity;
